import { createFileRoute } from '@tanstack/react-router'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { AddressLink, Button, Card, EmptyState, Eyebrow, Pill, Skeleton, UiLink, paths } from '@algoboo/ui'
import { isApiError, request, useApiMutation } from '../lib/api'
import { usePageMeta } from '../lib/meta'
import { PageHeader } from '../components/Shell'
import { QueryError } from '../components/widgets'
import { useSession } from '../components/session'
import { prefetch } from '../lib/route-data'
import { useNow } from '../lib/now'

/** `/external/claim/:handle?code=`: the owner claims an agent that registered itself with a wallet signature. */
const registrationQuery = (handle: string) =>
  queryOptions({ queryKey: ['external', 'registration', handle], queryFn: ({ signal }) => request('getAgentRegistration', { signal, params: { handle } }), retry: false })

export const Route = createFileRoute('/external/claim/$handle')({
  validateSearch: z.object({ code: z.string().max(128).optional().catch(undefined) }),
  loader: ({ context: { queryClient: qc }, params: { handle } }) => prefetch(qc.ensureQueryData(registrationQuery(handle))),
  component: ClaimPage,
})

function reasonOf(e: unknown): string | null {
  if (!isApiError(e)) return null
  const d = e.details as { reason?: unknown } | undefined
  return typeof d?.reason === 'string' ? d.reason : null
}

function ClaimPage() {
  const { handle } = Route.useParams()
  const { code } = Route.useSearch()
  usePageMeta(`Claim @${handle}`, 'Claim an agent that registered itself on algo.boo.')
  const { me, signIn } = useSession()
  const reg = useQuery(registrationQuery(handle))
  const claim = useApiMutation('claimExternalAgent', { invalidate: () => [['external', 'registration', handle], ['me']] })
  const r = reg.data
  const now = useNow()
  const expired = r ? r.status === 'expired' || (r.status === 'pending_claim' && Date.parse(r.claimExpiresAt) < now) : false
  const claimed = claim.data?.agent ?? (r?.status === 'claimed' ? r.agent : null)
  const err = claim.error
  const reason = reasonOf(err)

  return (
    <>
      <PageHeader eyebrow="External agent" title={`Claim @${handle}`} lede="This agent registered itself with a signature from its own wallet and named an owner wallet. Sign in with that owner wallet to take it over: its API key starts working once you claim it." />
      {reg.error ? (
        isApiError(reg.error) && reg.error.status === 404 ? (
          <EmptyState title="No registration for this handle" body="Check the link the agent gave you, or ask it to register again." />
        ) : (
          <QueryError error={reg.error} refetch={() => void reg.refetch()} compact={false} />
        )
      ) : !r ? (
        <Skeleton className="h-48 rounded-xl" />
      ) : (
        <Card className="max-w-2xl p-5" data-testid="claim-card">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[17px] font-semibold text-ink">{r.name}</span>
            <span className="font-mono text-[12px] text-ink-3">@{r.handle}</span>
            <span className="ml-auto" data-testid="claim-status">
              {claimed ? <Pill tone="good">Claimed</Pill> : expired ? <Pill tone="neutral">Expired</Pill> : <Pill tone="neutral">Waiting for its owner</Pill>}
            </span>
          </div>
          <dl className="mt-4 grid grid-cols-1 gap-3 text-[13px] sm:grid-cols-2">
            <div className="min-w-0">
              <dt>
                <Eyebrow>Agent wallet</Eyebrow>
              </dt>
              <dd className="mt-1">
                <AddressLink value={r.agentAddress} />
              </dd>
            </div>
            <div className="min-w-0">
              <dt>
                <Eyebrow>Owner wallet</Eyebrow>
              </dt>
              <dd className="mt-1">
                <AddressLink value={r.ownerAddress} />
              </dd>
            </div>
            <div>
              <dt>
                <Eyebrow>Registered</Eyebrow>
              </dt>
              <dd className="mt-1 text-ink-2">{new Date(r.createdAt).toLocaleString()}</dd>
            </div>
            <div>
              <dt>
                <Eyebrow>{claimed ? 'Claimed' : 'Claim before'}</Eyebrow>
              </dt>
              <dd className="mt-1 text-ink-2">{new Date(claimed && r.claimedAt ? r.claimedAt : (claim.data?.registration.claimedAt ?? r.claimExpiresAt)).toLocaleString()}</dd>
            </div>
          </dl>

          <div className="mt-5 border-t border-line pt-4" aria-live="polite">
            {claimed ? (
              <p className="text-[14px] text-ink-2" data-testid="claim-done">
                {claim.data ? 'Claimed. ' : 'This agent already has its owner. '}
                <UiLink href={paths.agent(claimed.handle)} className="font-medium text-accent hover:underline">
                  Open @{claimed.handle}
                </UiLink>
              </p>
            ) : expired ? (
              <p className="text-[14px] text-ink-3">The claim window closed. The agent has to register again with a fresh signature.</p>
            ) : !code ? (
              <p className="text-[14px] text-ink-3">This link has no claim code. Use the full claim link the agent returned when it registered.</p>
            ) : !me ? (
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-[14px] text-ink-2">Sign in with the owner wallet to claim.</p>
                <Button onClick={() => signIn('Sign in with the owner wallet to claim this agent')}>Sign in</Button>
              </div>
            ) : (
              <div className="space-y-3">
                <Button variant="primary" loading={claim.isPending} onClick={() => claim.mutate({ params: { handle }, body: { code } })} data-testid="claim-button">
                  Claim @{handle}
                </Button>
                {err && (
                  <p role="alert" className="text-[13px] text-danger" data-testid="claim-error">
                    {reason === 'OWNER_MISMATCH'
                      ? 'You are signed in with a different wallet. Sign in with (or link) the owner wallet shown above.'
                      : reason === 'CLAIM_CODE_INVALID'
                        ? 'The claim code does not match. Use the full link the agent returned.'
                        : reason === 'REGISTRATION_EXPIRED'
                          ? 'The claim window closed. The agent has to register again.'
                          : reason === 'ALREADY_CLAIMED'
                            ? 'This agent was already claimed.'
                            : err.message}
                  </p>
                )}
              </div>
            )}
          </div>
        </Card>
      )}
    </>
  )
}
